import React, { useEffect, useRef } from 'react';

/** Shared keyboard/focus behavior for staff confirmation and editing dialogs. */
const Dialog: React.FC<{
  children: React.ReactNode;
  className?: string;
  label: string;
  onClose: () => void;
  style?: React.CSSProperties;
}> = ({ children, className, label, onClose, style }) => {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const target = ref.current?.querySelector<HTMLElement>(
      'input, textarea, select, button, a[href], [tabindex="0"]',
    );
    (target ?? ref.current)?.focus();
    return () => previous?.focus();
  }, []);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      className={className}
      style={style}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close.current();
        }
        if (event.key !== 'Tab') return;
        const elements = Array.from(
          ref.current?.querySelectorAll<HTMLElement>(
            'a[href], button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]',
          ) ?? [],
        );
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (!first) {
          event.preventDefault();
          ref.current?.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }}
    >
      {children}
    </div>
  );
};

export default Dialog;
